// Field-group definitions for the ATLAS structured form. Generalises the original
// cross-border means-of-transport block (ATLAS box 21) so EVERY cluster in the Header (kopf)
// and Declarant (anmelder) sections renders in its own tab-aware fieldset.
//
// Kept dependency-free (no AtlasValidationPreview / axios import) so it can be unit-tested
// under Jest. The grouping logic only needs a field's `path`, hence the generic `{ path: string }`.

export interface FieldGroupDef {
  id: string;
  labelKey: string;       // i18n key
  fallbackLabel: string;  // shown if the i18n key is missing
  paths?: string[];       // exact full paths in this group
  prefixes?: string[];    // full-path prefixes in this group (covers array indices, nested objects)
}

export interface GroupedFields<T> {
  id: string;
  labelKey: string;
  fallbackLabel: string;
  fields: T[];
}

const K = 'documentProcessing.atlasForm.fieldGroups';

export const ATLAS_FIELD_GROUPS: Record<'kopf' | 'anmelder', FieldGroupDef[]> = {
  kopf: [
    {
      id: 'declaration',
      labelKey: `${K}.declaration`,
      fallbackLabel: 'Declaration & identifiers',
      paths: [
        'kopf.eoriNiederlassungsnummer',
        'kopf.lrn',
        'kopf.artderAnmeldung',
        'kopf.artderAusfuhranmeldung',
        'kopf.beteiligtenKonstellation',
        'kopf.bezugsnummer',
        'kopf.anzahlPositionen',
      ],
    },
    {
      id: 'customsOffices',
      labelKey: `${K}.customsOffices`,
      fallbackLabel: 'Customs offices',
      paths: [
        'kopf.ausfuhrzollstelleDienststellennummer',
        'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
        'kopf.gestellungszollstelle',
        'kopf.ausfuhrZollstelleFurDieErganzende',
      ],
    },
    {
      id: 'transaction',
      labelKey: `${K}.transaction`,
      fallbackLabel: 'Transaction & invoice',
      paths: [
        'kopf.artdesGeschafts',
        'kopf.angabenGeschaftsvorgangArt',
        'kopf.inRechnunggestellterGesamtbetrag',
        'kopf.rechnungswahrung',
        'kopf.datum',
      ],
    },
    {
      id: 'countries',
      labelKey: `${K}.countries`,
      fallbackLabel: 'Countries',
      paths: ['kopf.ausfuhrland', 'kopf.bestimmungsLand'],
    },
    {
      id: 'delivery',
      labelKey: `${K}.delivery`,
      fallbackLabel: 'Delivery terms (Incoterms)',
      prefixes: ['kopf.lieferbedingung'],
    },
    {
      id: 'transport',
      labelKey: `${K}.transport`,
      fallbackLabel: 'Transport',
      paths: [
        'kopf.containerIndikator',
        'kopf.inlandischerVerkehrszweig',
        'kopf.verkehrszweigAnDerGrenze',
        'kopf.gesamtRohmasse',
        'kopf.beforderungskostenZahlungsart',
      ],
      // NB: prefixes deliberately exclude bare 'kopf.beforderungsmittel' so the
      // cross-border 'beforderungsmittelderGrenze*' fields are NOT swallowed here.
      prefixes: ['kopf.beforderungsmittelBeimAbgang', 'kopf.beforderungsroute'],
    },
    {
      id: 'borderTransport',
      labelKey: 'documentProcessing.atlasForm.sections.borderTransport',
      fallbackLabel: 'Cross-border means of transport',
      paths: [
        'kopf.beforderungsmittelderGrenzeArt',
        'kopf.beforderungsmittelderGrenzeKennzeichen',
        'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
      ],
    },
    {
      id: 'goodsLocation',
      labelKey: `${K}.goodsLocation`,
      fallbackLabel: 'Goods location',
      prefixes: ['kopf.warenort', 'kopf.ladeort'],
    },
    {
      id: 'dates',
      labelKey: `${K}.dates`,
      fallbackLabel: 'Dates & timing',
      paths: [
        'kopf.zeitpunktderAnmeldung',
        'kopf.massgeblichesDatum',
        'kopf.kopfDatumdesAusgangs',
        'kopf.zeitpunktDerGestellung',
        'kopf.zeitpunktdesEndesderLadetatigkeit',
      ],
    },
    {
      id: 'security',
      labelKey: `${K}.security`,
      fallbackLabel: 'Security & special',
      paths: ['kopf.sicherheit', 'kopf.besondereUmstande'],
    },
    {
      id: 'authorisation',
      labelKey: `${K}.authorisation`,
      fallbackLabel: 'Authorisation',
      prefixes: ['kopf.bewilligung'],
    },
  ],
  anmelder: [
    {
      id: 'declarant',
      labelKey: `${K}.declarant`,
      fallbackLabel: 'Declarant',
      prefixes: ['kopf.anmelder.'],
    },
    {
      id: 'representative',
      labelKey: `${K}.representative`,
      fallbackLabel: 'Representative',
      prefixes: ['kopf.vertreter.'],
    },
    {
      id: 'subcontractor',
      labelKey: `${K}.subcontractor`,
      fallbackLabel: 'Subcontractor',
      prefixes: ['kopf.subunternehmer.'],
    },
  ],
};

function matchesGroup(def: FieldGroupDef, path: string): boolean {
  if (def.paths?.includes(path)) return true;
  if (def.prefixes?.some(p => path.startsWith(p))) return true;
  return false;
}

/**
 * Partition `fields` into the ordered groups defined for `section`. First matching group wins.
 * Any field matched by no group falls into a trailing `general` safety-net group (only appended
 * when non-empty). Guarantees a lossless, non-overlapping cover of the input, order preserved.
 */
export function groupFieldsForSection<T extends { path: string }>(
  section: string,
  fields: T[],
): GroupedFields<T>[] {
  const defs = (ATLAS_FIELD_GROUPS as Record<string, FieldGroupDef[]>)[section] ?? [];
  const groups: GroupedFields<T>[] = defs.map(d => ({
    id: d.id,
    labelKey: d.labelKey,
    fallbackLabel: d.fallbackLabel,
    fields: [],
  }));
  const general: GroupedFields<T> = {
    id: 'general',
    labelKey: `${K}.general`,
    fallbackLabel: 'Other',
    fields: [],
  };
  for (const field of fields) {
    const idx = defs.findIndex(d => matchesGroup(d, field.path));
    if (idx >= 0) groups[idx].fields.push(field);
    else general.fields.push(field);
  }
  return general.fields.length > 0 ? [...groups, general] : groups;
}

// ── Back-compat: cross-border helpers, single source of truth ──
const borderGroup = ATLAS_FIELD_GROUPS.kopf.find(g => g.id === 'borderTransport');
if (!borderGroup?.paths) {
  throw new Error('atlasFieldGroups: borderTransport group with paths is required');
}
export const BORDER_TRANSPORT_FIELDS: string[] = borderGroup.paths;

export const isBorderTransportField = (path: string): boolean =>
  BORDER_TRANSPORT_FIELDS.includes(path);
